import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Grid3X3, ArrowLeft, Trophy, History, Heart, Play, RotateCcw, ChevronUp, ChevronDown
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useMobileView } from "@/hooks/use-mobile-view";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const BASE_LEVEL_SCORE = 100;
const MAX_SPEED_BONUS = 50;

export default function VisualMemoryPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const isMobile = useMobileView();
  
  // Game State
  const [gameState, setGameState] = useState('intro'); // intro, flashing, input, feedback, gameover
  const [level, setLevel] = useState(1);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [pattern, setPattern] = useState([]);
  const [userPattern, setUserPattern] = useState([]);
  const [flashTime, setFlashTime] = useState(0); // Start time for speed bonus
  
  // Configuration for current level
  const [config, setConfig] = useState({ gridSize: 3, tiles: 3, flashDuration: 1000 });
const style = document.createElement('style');
style.textContent = `
  @keyframes retract {
    from { stroke-dashoffset: 0; }
    to { stroke-dashoffset: 100; }
  }
`;
document.head.appendChild(style);


  // Data
  const [leaderboard, setLeaderboard] = useState([]);
  const [attempts, setAttempts] = useState([]);

  // Refs
  const timeoutRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const savedUser = localStorage.getItem('user');
    if (savedUser) setUser(JSON.parse(savedUser));
    
    fetchLeaderboard();
    fetchAttempts();
    
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/visual-memory/leaderboard`);
      setLeaderboard(response.data);
    } catch (e) {
      console.error('Failed to fetch leaderboard', e);
    }
  };

  const fetchAttempts = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${API}/visual-memory/results`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttempts(response.data);
    } catch (e) {
      console.error('Failed to fetch attempts', e);
    }
  };

  const getLevelConfig = (lvl) => {
    // Levels 1-3 (Grid: 3x3)
    if (lvl <= 3) {
      return { 
        gridSize: 3, 
        tiles: lvl === 1 ? 3 : 4, 
        flashDuration: lvl === 3 ? 3000 : 5000 
      };
    }
    // Levels 4-7 (Grid: 4x4)
    if (lvl <= 7) {
      return { 
        gridSize: 4, 
        tiles: lvl + 1, // L4:5, L5:6, L6:6, L7:7 -- wait, specs say L4:5, L5:6, L6:6, L7:7
        // Adjust logic:
        // L4: 5
        // L5: 6
        // L6: 6
        // L7: 7
        tiles: lvl === 6 ? 6 : lvl + 1, 
        flashDuration: lvl === 6 ? 3000 : 5000 
      };
    }
    // Levels 8-12 (Grid: 5x5)
    if (lvl <= 12) {
      return { 
        gridSize: 5, 
        tiles: lvl, 
        flashDuration: 5000 
      };
    }
    // Levels 13+ (Grid: 6x6)
    // 13 tiles, Flash duration decreases by 0.5s after each round until 1s
    const decrease = (lvl - 13) * 500;
    return { 
      gridSize: 6, 
      tiles: 13, 
      flashDuration: Math.max(1000, 5000 - decrease) 
    };
  };

  const startGame = () => {
    setScore(0);
    setLives(3);
    setLevel(1);
    setGameState('intro'); // Reset to intro before starting first level? No, start immediately
    startLevel(1);
  };

  const startLevel = (lvl) => {
    const cfg = getLevelConfig(lvl);
    setConfig(cfg);
    setGameState('flashing');
    setUserPattern([]);
    
    // Generate Pattern
    const newPattern = [];
    const totalTiles = cfg.gridSize * cfg.gridSize;
    while (newPattern.length < cfg.tiles) {
      const rand = Math.floor(Math.random() * totalTiles);
      if (!newPattern.includes(rand)) {
        newPattern.push(rand);
      }
    }
    setPattern(newPattern);

    // Flash Timer
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setGameState('input');
      setFlashTime(Date.now());
    }, cfg.flashDuration);
  };

  const handleTileClick = (index) => {
    if (gameState !== 'input') return;

    // Check if already selected
    if (userPattern.includes(index)) return;

    const isCorrect = pattern.includes(index);
    const newUserPattern = [...userPattern, index];
    setUserPattern(newUserPattern);

    if (isCorrect) {
      // Correct Click
      // Check if level complete
      if (newUserPattern.filter(i => pattern.includes(i)).length === pattern.length) {
        handleLevelComplete();
      }
    } else {
      // Incorrect Click
      handleMistake();
    }
  };

  const handleLevelComplete = () => {
    setGameState('feedback');
    
    // Calculate Score
    const timeTaken = (Date.now() - flashTime) / 1000;
    const speedBonus = timeTaken <= 2 ? MAX_SPEED_BONUS : Math.max(0, MAX_SPEED_BONUS - Math.floor(timeTaken - 2) * 10);
    const levelScore = level * BASE_LEVEL_SCORE;
    const roundScore = levelScore + speedBonus;
    
    setScore(prev => prev + roundScore);
    toast.success(`Level ${level} Complete! +${roundScore} pts`);

    // Next Level Delay
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      const nextLvl = level + 1;
      setLevel(nextLvl);
      startLevel(nextLvl);
    }, 1000);
  };

  const handleMistake = () => {
    const newLives = lives - 1;
    setLives(newLives);
    setGameState('feedback'); // Show red flash
    
    if (newLives <= 0) {
      endGame();
    } else {
      toast.error("Wrong tile! Try again.");
      // Retry same level with new pattern
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        startLevel(level);
      }, 1000);
    }
  };

  const endGame = async () => {
    setGameState('gameover');
    if (user) {
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/visual-memory/results`, {
          score: score,
          level_reached: level
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchLeaderboard();
        fetchAttempts();
        toast.error("Game Over!");
      } catch (e) {
        console.error("Failed to save score", e);
      }
    }
  };

  // Render Grid
  const renderGrid = () => {
    const tiles = [];
    const totalTiles = config.gridSize * config.gridSize;
    
    for (let i = 0; i < totalTiles; i++) {
      let status = 'default'; // default, active, success, error
      
      if (gameState === 'flashing' && pattern.includes(i)) {
        status = 'active';
      } else if (userPattern.includes(i)) {
        if (pattern.includes(i)) {
          status = 'success';
        } else {
          status = 'error';
        }
      }

      tiles.push(
        <button
          key={i}
          onClick={() => handleTileClick(i)}
          className={`
            aspect-square rounded-lg transition-all duration-200 transform
            ${status === 'default' ? 'bg-slate-800 hover:bg-slate-700' : ''}
            ${status === 'active' ? 'bg-indigo-500 shadow-lg shadow-indigo-500/50 scale-105' : ''}
            ${status === 'success' ? 'bg-emerald-500 shadow-lg shadow-emerald-500/50' : ''}
            ${status === 'error' ? 'bg-red-500 shadow-lg shadow-red-500/50 animate-shake' : ''}
            ${gameState !== 'input' ? 'cursor-default' : 'cursor-pointer active:scale-95'}
          `}
          disabled={gameState !== 'input'}
        />
      );
    }

    return (
      <div className="relative w-full max-w-md mx-auto aspect-square">
        {gameState === 'flashing' && (
          <svg className="absolute inset-0 w-full h-full pointer-events-none z-10 overflow-visible">
            <rect 
              x="0" y="0" width="100%" height="100%" 
              rx="12" ry="12"
              fill="none" 
              stroke="#ef4444" 
              strokeWidth="4"
              pathLength="100"
              style={{
                strokeDasharray: 100,
                strokeDashoffset: 0,
                animation: `retract ${config.flashDuration}ms linear forwards`
              }}
            />
          </svg>
        )}
        <div 
          className="grid gap-3 w-full h-full p-4 bg-slate-900/50 rounded-2xl border border-slate-800"
          style={{ gridTemplateColumns: `repeat(${config.gridSize}, 1fr)` }}
        >
          {tiles}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#020617] text-white font-sans selection:bg-indigo-500/30">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 p-4 flex justify-between items-center bg-[#020617]/90 backdrop-blur border-b border-slate-800">
        <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/')} className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-5 h-5 mr-2" /> Exit
            </Button>
            <div className="text-sm text-slate-400 hidden sm:block">
                {user?.username}
            </div>
        </div>
        <div className="flex gap-6 font-mono text-sm">
           <div className="flex items-center gap-2">
             <Trophy className="w-4 h-4 text-yellow-500" />
             <span className="text-xl font-bold">{score}</span>
           </div>
           <div className="flex items-center gap-2 text-red-500">
             <Heart className="w-4 h-4 fill-current" />
             <span className="text-xl font-bold">{lives}</span>
           </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className={`grid ${isMobile ? 'grid-cols-1' : 'lg:grid-cols-3'} gap-8`}>
            
            {/* Game Area */}
            <div className={`${isMobile ? 'w-full' : 'lg:col-span-2'}`}>
                <div className="flex flex-col items-center justify-center p-6 relative w-full bg-slate-900/30 rounded-3xl border border-slate-800/50 min-h-[500px]">
                    
                    {/* Intro State */}
                    {gameState === 'intro' && (
                        <div className="text-center space-y-8 animate-in fade-in zoom-in duration-500">
                            <div className="w-24 h-24 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl flex items-center justify-center mx-auto shadow-2xl shadow-indigo-500/20">
                                <Grid3X3 className="w-12 h-12 text-white" />
                            </div>
                            <div>
                                <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-4">VISUAL MEMORY</h1>
                                <p className="text-slate-400 text-lg max-w-xs mx-auto">
                                    Memorize the pattern. <br/>
                                    Recall it perfectly.
                                </p>
                            </div>
                            <Button onClick={startGame} className="w-full py-8 text-xl font-bold bg-white text-black hover:bg-slate-200 rounded-xl uppercase tracking-wider shadow-xl shadow-white/10">
                                Start Test
                            </Button>
                        </div>
                    )}

                    {/* Game Over State */}
                    {gameState === 'gameover' && (
                        <div className="text-center space-y-8 animate-in zoom-in duration-500">
                            <div className="text-6xl mb-4">💀</div>
                            <h2 className="text-4xl font-black">GAME OVER</h2>
                            
                            <div className="bg-slate-900 rounded-2xl p-8 border border-slate-800 w-full max-w-xs mx-auto space-y-4">
                                <div>
                                    <div className="text-slate-500 text-sm uppercase tracking-wider mb-1">Final Score</div>
                                    <div className="text-5xl font-mono font-bold text-white">{score}</div>
                                </div>
                                <div className="border-t border-slate-800 pt-4">
                                    <div className="text-slate-500 text-sm uppercase tracking-wider mb-1">Level Reached</div>
                                    <div className="text-2xl font-mono font-bold text-indigo-400">{level}</div>
                                </div>
                            </div>

                            <Button onClick={startGame} className="w-full py-6 px-8 text-lg font-bold bg-white text-black hover:bg-slate-200">
                                <RotateCcw className="w-5 h-5 mr-2" /> Play Again
                            </Button>
                        </div>
                    )}

                    {/* Active Game State */}
                    {(gameState === 'flashing' || gameState === 'input' || gameState === 'feedback') && (
                        <div className="w-full max-w-lg space-y-6 animate-in fade-in">
                            <div className="text-center">
                                <div className="text-indigo-400 font-mono text-sm uppercase tracking-widest mb-2">Level {level}</div>
                                <div className="text-2xl font-bold text-white">
                                    {gameState === 'flashing' ? 'Memorize!' : gameState === 'input' ? 'Recall!' : '...'}
                                </div>
                            </div>
                            
                            {renderGrid()}
                        </div>
                    )}
                </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Leaderboard */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Trophy className="w-5 h-5 text-yellow-500" />
                  <h3 className="font-heading font-bold text-lg">Global Leaderboard</h3>
                </div>
                <ScrollArea className="h-64">
                  {leaderboard.length > 0 ? (
                    <div className="space-y-2">
                      {leaderboard.slice(0, 10).map((entry, idx) => (
                        <div 
                          key={idx}
                          className={`flex items-center justify-between p-3 rounded-lg ${
                            entry.username === user?.username ? 'bg-indigo-500/20 border border-indigo-500/30' : 'bg-slate-800/50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                              idx === 0 ? 'bg-yellow-500 text-black' :
                              idx === 1 ? 'bg-slate-400 text-black' :
                              idx === 2 ? 'bg-amber-700 text-white' :
                              'bg-slate-700 text-slate-300'
                            }`}>
                              {entry.rank}
                            </span>
                            <span className="font-medium truncate max-w-24">{entry.username}</span>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-indigo-400">{entry.score}</div>
                            <div className="text-xs text-slate-500">Lvl {entry.level_reached}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-slate-500 py-8">
                      No entries yet. Be the first!
                    </div>
                  )}
                </ScrollArea>
              </div>

              {/* Recent Attempts */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <History className="w-5 h-5 text-indigo-400" />
                  <h3 className="font-heading font-bold text-lg">Your History</h3>
                </div>
                <ScrollArea className="h-64">
                  {attempts.length > 0 ? (
                    <div className="space-y-2">
                      {attempts.slice(0, 15).map((attempt, idx) => {
                        const prevAttempt = attempts[idx + 1];
                        const isImprovement = prevAttempt && attempt.score > prevAttempt.score;
                        const isWorst = prevAttempt && attempt.score < prevAttempt.score;
                        
                        return (
                          <div 
                            key={attempt.id}
                            className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50"
                          >
                            <div className="flex items-center gap-2">
                              {isImprovement && <ChevronUp className="w-4 h-4 text-emerald-400" />}
                              {isWorst && <ChevronDown className="w-4 h-4 text-red-400" />}
                              {!isImprovement && !isWorst && <div className="w-4" />}
                              <span className="text-slate-400 text-sm">
                                {new Date(attempt.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            <div className="text-right">
                                <div className="font-mono font-bold text-indigo-400">{attempt.score}</div>
                                <div className="text-xs text-slate-500">Lvl {attempt.level_reached}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center text-slate-500 py-8">
                      No attempts yet. Start testing!
                    </div>
                  )}
                </ScrollArea>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
